import React, { useState, useEffect, useCallback } from 'react';
import { CalendarCheck, Sun, CheckCircle, XCircle, AlertCircle, Save, Palmtree, Trash2 } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import Modal from '../../components/Modal';
const API = process.env.REACT_APP_BACKEND_URL;

function today() {
  return new Date().toISOString().split('T')[0];
}

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {message}
    </div>
  );
}

export default function AdminAttendance() {
  const [batches, setBatches] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today());
  const [selectedBatch, setSelectedBatch] = useState('');
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({}); // { student_id: 'present'|'absent' }
  const [holiday, setHoliday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ type: 'public', label: '' });
  const [toast, setToast] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/batches`, { headers: getAuthHeaders() })
      .then(r => setBatches(r.data))
      .catch(console.error);
  }, []);

  const loadAttendance = useCallback(async () => {
    if (!selectedDate || !selectedBatch) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/attendance?date=${selectedDate}&batch_id=${selectedBatch}`,
        { headers: getAuthHeaders() }
      );
      setStudents(data.students);
      setHoliday(data.holiday);
      // Init attendance: default absent, override with saved records
      const init = {};
      data.students.forEach(s => { init[s.id] = 'absent'; });
      Object.entries(data.records).forEach(([sid, status]) => { init[sid] = status; });
      setAttendance(init);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedDate, selectedBatch]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  const toggle = (studentId) => {
    setAttendance(prev => ({ ...prev, [studentId]: prev[studentId] === 'present' ? 'absent' : 'present' }));
  };

  const markAll = (status) => {
    const next = {};
    students.forEach(s => { next[s.id] = status; });
    setAttendance(next);
  };

  const saveAttendance = async () => {
    setSaving(true);
    try {
      const records = students.map(s => ({ student_id: s.id, status: attendance[s.id] || 'absent' }));
      await axios.post(`${API}/api/admin/attendance/save`,
        { date: selectedDate, batch_id: selectedBatch, records },
        { headers: getAuthHeaders() }
      );
      setToast({ message: 'Attendance saved!', type: 'success' });
    } catch { setToast({ message: 'Could not save. Try again.', type: 'error' }); }
    finally { setSaving(false); }
  };

  const markHoliday = async () => {
    try {
      const { data } = await axios.post(`${API}/api/admin/holidays`,
        { date: selectedDate, batch_id: selectedBatch || null, ...holidayForm },
        { headers: getAuthHeaders() }
      );
      setHoliday(data);
      setShowHolidayForm(false);
      setToast({ message: 'Holiday marked!', type: 'success' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Could not mark holiday.', type: 'error' });
    }
  };

  const removeHoliday = async () => {
    if (!holiday) return;
    try {
      await axios.delete(`${API}/api/admin/holidays/${holiday.id}`, { headers: getAuthHeaders() });
      setHoliday(null);
      setToast({ message: 'Holiday removed.', type: 'success' });
    } catch { setToast({ message: 'Could not remove holiday.', type: 'error' }); }
  };

  const presentCount = Object.values(attendance).filter(v => v === 'present').length;
  const absentCount = students.length - presentCount;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Daily</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Attendance</h1>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#57534E] mb-1.5">Date</label>
            <input
              data-testid="attendance-date-input"
              type="date" value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl px-4 py-2.5 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#57534E] mb-1.5">Batch</label>
            <select
              data-testid="attendance-batch-select"
              value={selectedBatch} onChange={e => setSelectedBatch(e.target.value)}
              className="bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl px-4 py-2.5 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm"
            >
              <option value="">Select a batch...</option>
              {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Prompt */}
      {!selectedBatch && (
        <div className="bg-white rounded-3xl p-10 text-center shadow-card border border-[#E7E5E4]/50">
          <CalendarCheck className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-heading font-medium text-[#292524]">Select a batch to mark attendance</p>
          <p className="text-[#78716C] text-sm font-body mt-1">Choose a date and batch above to get started.</p>
        </div>
      )}

      {selectedBatch && loading && (
        <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      )}

      {/* Holiday banner */}
      {selectedBatch && !loading && holiday && (
        <div className="bg-amber-50 border-2 border-[#F59E0B]/40 rounded-3xl p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Sun className="w-6 h-6 text-[#F59E0B]" strokeWidth={1.5} />
            <div>
              <p className="font-heading font-medium text-[#292524]">
                {holiday.label || (holiday.type === 'public' ? 'Public Holiday' : 'Personal Holiday')}
              </p>
              <p className="text-xs text-[#78716C] font-body capitalize">{holiday.type} holiday — no attendance needed</p>
            </div>
          </div>
          <button
            data-testid="remove-holiday-button"
            onClick={removeHoliday}
            className="flex items-center gap-2 text-red-500 hover:text-red-700 text-sm font-body transition-colors"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Remove
          </button>
        </div>
      )}

      {/* Student list */}
      {selectedBatch && !loading && !holiday && students.length === 0 && (
        <div className="bg-white rounded-3xl p-10 text-center shadow-card border border-[#E7E5E4]/50">
          <p className="font-heading font-medium text-[#292524]">No students in this batch</p>
          <p className="text-[#78716C] text-sm font-body mt-1">Assign students to this batch from the Students page.</p>
        </div>
      )}

      {selectedBatch && !loading && !holiday && students.length > 0 && (
        <div className="space-y-4">
          {/* Summary + quick actions */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5 text-sm text-green-600 font-body bg-green-50 rounded-full px-3 py-1">
                <CheckCircle className="w-3.5 h-3.5" strokeWidth={2} /> {presentCount} Present
              </span>
              <span className="flex items-center gap-1.5 text-sm text-red-500 font-body bg-red-50 rounded-full px-3 py-1">
                <XCircle className="w-3.5 h-3.5" strokeWidth={2} /> {absentCount} Absent
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => markAll('present')} className="text-xs text-green-600 font-body hover:underline">Mark All Present</button>
              <span className="text-[#E7E5E4]">|</span>
              <button onClick={() => markAll('absent')} className="text-xs text-red-500 font-body hover:underline">Mark All Absent</button>
            </div>
          </div>

          {/* Student rows */}
          <div className="bg-white rounded-3xl shadow-card border border-[#E7E5E4]/50 overflow-hidden">
            {students.map((student, i) => {
              const status = attendance[student.id] || 'absent';
              return (
                <div
                  key={student.id}
                  data-testid={`attendance-row-${student.id}`}
                  className={`flex items-center justify-between px-5 py-4 transition-colors
                    ${i !== students.length - 1 ? 'border-b border-[#E7E5E4]/60' : ''}
                    ${status === 'present' ? 'bg-green-50/30' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-heading font-semibold shrink-0
                      ${status === 'present' ? 'bg-green-100 text-green-700' : 'bg-[#F5F5F4] text-[#A8A29E]'}`}>
                      {student.name?.[0]?.toUpperCase()}
                    </div>
                    <p className="font-body font-medium text-[#292524] text-sm">{student.name}</p>
                  </div>

                  <button
                    data-testid={`toggle-attendance-${student.id}`}
                    onClick={() => toggle(student.id)}
                    className={`rounded-full px-5 py-2 text-sm font-heading font-medium transition-all duration-200
                      ${status === 'present'
                        ? 'bg-green-500 text-white shadow-sm hover:bg-green-600'
                        : 'bg-[#F5F5F4] text-[#A8A29E] hover:bg-red-50 hover:text-red-500'
                      }`}
                  >
                    {status === 'present' ? 'Present' : 'Absent'}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 flex-wrap">
            <button
              data-testid="save-attendance-button"
              onClick={saveAttendance}
              disabled={saving}
              className="flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-6 py-3 font-heading font-medium text-sm shadow-button hover:-translate-y-0.5 transition-all disabled:opacity-60"
            >
              <Save className="w-4 h-4" strokeWidth={1.5} />
              {saving ? 'Saving...' : 'Save Attendance'}
            </button>
            <button
              data-testid="mark-holiday-button"
              onClick={() => setShowHolidayForm(true)}
              className="flex items-center gap-2 border-2 border-[#E7E5E4] text-[#57534E] hover:border-[#F59E0B] hover:text-[#D97706] rounded-full px-6 py-3 font-heading font-medium text-sm transition-all"
            >
              <Sun className="w-4 h-4" strokeWidth={1.5} />
              Mark as Holiday
            </button>
          </div>
        </div>
      )}

      {showHolidayForm && (
        <Modal
          title="Mark as Holiday"
          onClose={() => setShowHolidayForm(false)}
          footer={
            <div className="flex gap-3">
              <button onClick={() => setShowHolidayForm(false)}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm">
                Cancel
              </button>
              <button onClick={markHoliday}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button">
                Mark Holiday
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Holiday Type</label>
              <select
                value={holidayForm.type} onChange={e => setHolidayForm(f => ({ ...f, type: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body"
              >
                <option value="public">Public Holiday</option>
                <option value="personal">Personal Holiday</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Label (optional)</label>
              <input
                type="text" placeholder='e.g. "Diwali"'
                value={holidayForm.label} onChange={e => setHolidayForm(f => ({ ...f, label: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body"
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
